import { forwardRef, type SelectHTMLAttributes } from 'react';
import { useCars, useDrivers } from '../../hooks/useApi';
import { carLabel, driverName } from '../../utils/helpers';
import { Select } from '../ui/Input';
import { t } from '../../i18n';

type BaseProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  label?: string;
  error?: string;
  placeholder?: string;
  wrapperClassName?: string;
};

/**
 * Car picker. `onlyActive` limits to cars that can be assigned.
 * The select is remounted when options arrive so a pre-set form value is applied.
 */
export const CarSelect = forwardRef<HTMLSelectElement, BaseProps & { onlyActive?: boolean }>(
  ({ onlyActive = false, placeholder = t('Vyberte vozidlo…'), ...props }, ref) => {
    const { data, isLoading } = useCars({ page_size: 500, status: onlyActive ? 'active' : undefined });
    const options = (data?.cars ?? []).map((car) => ({ value: String(car.id), label: carLabel(car) }));
    return <Select key={isLoading ? 'loading' : 'ready'} ref={ref} options={options} placeholder={isLoading ? t('Načítava sa…') : placeholder} {...props} />;
  },
);
CarSelect.displayName = 'CarSelect';

export const DriverSelect = forwardRef<HTMLSelectElement, BaseProps & { onlyActive?: boolean }>(
  ({ onlyActive = true, placeholder = t('Vyberte vodiča…'), ...props }, ref) => {
    const { data, isLoading } = useDrivers({ page_size: 500, is_active: onlyActive ? true : undefined });
    const options = (data?.drivers ?? []).map((d) => ({
      value: String(d.id),
      label: `${driverName(d)}${d.employee_id ? ` (${d.employee_id})` : ''}`,
    }));
    return <Select key={isLoading ? 'loading' : 'ready'} ref={ref} options={options} placeholder={isLoading ? t('Načítava sa…') : placeholder} {...props} />;
  },
);
DriverSelect.displayName = 'DriverSelect';
